"use client";

import { useMemo } from "react";
import { useUser } from "@clerk/nextjs";
import { PassType, EffectivePassInfo } from "../types/pass";
import { getEffectivePassType, hasFeatureAccess, isAdFree as isAdFreeUtil } from "../lib/pass-utils";

interface UseEffectivePassReturn extends EffectivePassInfo {
  /** 이용권 만료일 (구매 패스) */
  passExpiresAt: string | null;
  /** Starter 구매일 (평생 접근용) */
  starterPurchasedAt: string | null;
  /** 무료체험 만료일 */
  freeTrialExpiresAt: string | null;
  /** 사용자 로드 완료 여부 */
  isLoaded: boolean;
  /** 로그인 여부 */
  isSignedIn: boolean | undefined;
  /** Premium 이상 이용권 여부 */
  isPremiumOrAbove: boolean;
  /** 광고 미노출 여부 */
  isAdFree: boolean;
  /** 특정 기능 접근 가능 여부 */
  hasAccess: (featureName: string) => boolean;
}

/**
 * 구매 + 체험 메타데이터를 종합하여 실질적 패스 정보를 반환하는 훅
 * 기존의 반복되는 메타데이터 읽기 패턴을 통합
 */
export function useEffectivePass(): UseEffectivePassReturn {
  const { user, isLoaded, isSignedIn } = useUser();

  const result = useMemo(() => {
    const passType = (user?.publicMetadata?.passType as PassType | undefined) || 'free';
    const passExpiresAt = (user?.publicMetadata?.passExpiresAt as string | null) ?? null;
    const starterPurchasedAt = (user?.publicMetadata?.starterPurchasedAt as string | null) ?? null;
    const freeTrialStartedAt = (user?.publicMetadata?.freeTrialStartedAt as string | null) ?? null;
    const freeTrialExpiresAt = (user?.publicMetadata?.freeTrialExpiresAt as string | null) ?? null;
    const freeTrialTier = (user?.publicMetadata?.freeTrialTier as PassType | undefined) ?? undefined;

    const effectiveInfo = getEffectivePassType({
      passType,
      passExpiresAt,
      starterPurchasedAt,
      freeTrialStartedAt,
      freeTrialExpiresAt,
      freeTrialTier,
    });

    const isPremiumOrAbove = effectiveInfo.isActive &&
      ['premium', 'complete', 'intelligence'].includes(effectiveInfo.passType);

    const adFree = effectiveInfo.isActive && isAdFreeUtil(effectiveInfo.passType, starterPurchasedAt);

    const hasAccess = (featureName: string) => {
      if (!effectiveInfo.isActive) return false;
      return hasFeatureAccess(effectiveInfo.passType, featureName, starterPurchasedAt);
    };

    return {
      ...effectiveInfo,
      passExpiresAt,
      starterPurchasedAt,
      freeTrialExpiresAt,
      isPremiumOrAbove,
      isAdFree: adFree,
      hasAccess,
    };
  }, [user]);

  return {
    ...result,
    isLoaded,
    isSignedIn,
  };
}
